import {useForm, UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Grid,
  Group, Loader,
  Modal,
  ModalProps,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import React, {useCallback, useMemo, useState} from "react";
import {useServiceEventForm} from "./useServiceEventForm";
import {
  CompanyEntity,
  Entity,
  ServiceEventCloseoutPolicy,
  serviceEventTargetMode,
  ServiceEventTargetMode,
  ServiceEventType, sitesSelector,
  toServiceEventCloseoutPolicy,
  toServiceEventTargetMode,
  toServiceEventType, useGetAllSitesQuery
} from "@ots-frontend/ots-data";
import {Map, MAP_DEFAULT_CENTER, useMapLoader} from "../../map/Map/Map";
import {ServiceEventTypeSelect} from "../../select/entity/ServiceEventTypeSelect/ServiceEventTypeSelect";
import {DateInput} from "../../date/DateInput/DateInput";
import {useDisclosure} from "@mantine/hooks";
import {IconPlus, IconTrash} from "@tabler/icons-react";
import {EntityMultiSelect, EntityMultiSelectProps} from "../../select/entity/EntityMultiSelect/EntityMultiSelect";
import {EntityTypeSelect} from "../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {
  ServiceEventTargetModeSelect
} from "../../select/entity/ServiceEventTargetModeSelect/ServiceEventTargetModeSelect";
import {
  EditablePolygonCompleteEvent,
  EditablePolygonDeleteEvent,
  PolygonEditor
} from "../../map/polygon/PolygonEditor/PolygonEditor";
import {
  ServiceEventCloseoutPolicySelect
} from "../../select/entity/ServiceEventCloseoutPolicySelect/ServiceEventCloseoutPolicySelect";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {GeoJSON} from "geojson";
import LatLngLiteral = google.maps.LatLngLiteral;
import {SiteMarkerClustererMemo} from "@ots-frontend/ots-ui";

export interface ServiceEventFormProps {
  options?: ServiceEventFormOptions;
  defaultValues?: ServiceEventFormDefaultValues;
  onSubmit?: (values: ServiceEventFormValues) => void;
  submitting?: boolean;
}

export interface ServiceEventTargetFormValues {
  objectId: string;
  objectType: string;
  objectName?: string;
  mode: ServiceEventTargetMode;
}

export interface ServiceEventTargetRawFormValues {
  objectId: string;
  objectType: string;
  objectName?: string;
  mode: string;
}

export interface ServiceEventFormOptions {
  regionId?: string;
  googleMapsApiKey?: string;
}

export interface ServiceEventFormValues {
  eventType: ServiceEventType
  company: CompanyEntity | string
  name: string
  severityLevel?: number
  startDate: Date
  endDate?: Date
  closeoutPolicy: ServiceEventCloseoutPolicy
  note?: string
  targets?: ServiceEventTargetFormValues[]
  footprintGeom?: GeoJSON.FeatureCollection
}

export interface ServiceEventFormDefaultValues extends Partial<ServiceEventFormValues> {

}

export interface ServiceEventFormRawValues {
  eventType?: string
  company?: string
  name?: string
  severityLevel?: number
  startDate?: Date
  endDate?: Date
  closeoutPolicy?: string
  note?: string
  targets?: ServiceEventTargetRawFormValues[]
}

export type ServiceEventFormType = UseFormReturnType<ServiceEventFormRawValues, (values: ServiceEventFormRawValues) => ServiceEventFormRawValues>

export const ServiceEventForm = (props: ServiceEventFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false,
  } = props;

  const [targetSelectOpened, targetSelectHandler] = useDisclosure(false);

  const [_map, setMap] = useState<google.maps.Map | null>(null);
  const [polygons, setPolygons] = useState<{ paths: LatLngLiteral[] }[]>([]);

  const {t} = useTranslation(["common"]);

  // for the map
  const selectSites = useMemo(() => {
    return sitesSelector()
  }, []);

  const {sites, isLoading} = useGetAllSitesQuery(options?.regionId, {
    selectFromResult: (result) => ({
      sites: selectSites(result, {}).filter(s => s.location),
      isLoading: result.isLoading
    })
  });

  const form = useServiceEventForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        eventType: toServiceEventType(values.eventType!),
        company: values.company!,
        name: values.name!,
        severityLevel: values.severityLevel,
        startDate: values.startDate!,
        endDate: values.endDate,
        closeoutPolicy: toServiceEventCloseoutPolicy(values.closeoutPolicy!),
        note: values.note,
        targets: values.targets?.map(t => ({
          objectId: t.objectId,
          objectType: t.objectType,
          objectName: t.objectName,
          mode: toServiceEventTargetMode(t.mode),
        })),
        footprintGeom: polygonPathsToGeoJSON(polygons.map(p => p.paths)),
      });
    }
  }, [form, onSubmit, polygons]);

  const {isLoaded: isMapLoaded} = useMapLoader({
    googleMapsApiKey: options?.googleMapsApiKey ?? "",
  });

  const center: google.maps.LatLngLiteral = useMemo(() => {
    return MAP_DEFAULT_CENTER
  }, [])

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onMapUnmount = useCallback((map: google.maps.Map) => {
    setMap(null);
  }, []);

  const onPolygonComplete = useCallback((event: EditablePolygonCompleteEvent) => {
    setPolygons((p) => [...p, {paths: event.paths}]);
  }, []);

  const onPolygonDelete = useCallback((event: EditablePolygonDeleteEvent) => {
    setPolygons((p) => p.filter((_, i) => i !== event.index));
  }, [])

  const onTargetAdd = useCallback((target: { entities: Entity[], mode: ServiceEventTargetMode } | undefined) => {
    if (target) {
      for (const entity of target.entities) {
        form.insertListItem('targets', {
          objectId: entity.id,
          objectType: entity.entityType,
          objectName: entity.displayName,
          mode: target.mode.code,
        })
      }
    }
    targetSelectHandler.close()
  }, [form, targetSelectHandler])

  const onTargetRemove = useCallback((idToRemove: string) => {
    const indexes: number[] = []

    form.values.targets.forEach((t, index) => {
      if (t.objectId === idToRemove) {
        indexes.push(index)
      }
    })

    indexes.forEach((index) => {
      form.removeListItem('targets', index)
    })

  }, [form])


  const targetFields = form.values.targets?.map((t, index) => (
    <Group key={`targets_${t.objectId}`} spacing="xs" mb="md" noWrap>
      <TextInput
        {...form.getInputProps(`targets.${index}.objectName`)}
        disabled={true}/>
      <ServiceEventTargetModeSelect
        {...form.getInputProps(`targets.${index}.mode`)}
        disabled={true}/>
      <ActionIcon>
        <IconTrash size="1.2rem" onClick={() => onTargetRemove(t.objectId)}/>
      </ActionIcon>
    </Group>
  ));

  return (
    <Grid grow gutter="md">
      <Grid.Col span={6}>
        <form onSubmit={form.onSubmit(onFormSubmit)}>
          <Grid grow gutter="xs">
            <Grid.Col span={12}>
              <CompanySelect
                size="xs"
                label={t("common.entity.serviceevent.form.field.company", "Company")}
                placeholder={t<string>("common.entity.serviceevent.form.field.company", "Company")}
                clearable
                withAsterisk
                required
                {...form.getInputProps("company")} />
            </Grid.Col>
            <Grid.Col span={12}>
              <ServiceEventTypeSelect
                size="xs"
                label={t("common.entity.serviceevent.form.field.eventtype", "Event Type")}
                placeholder={t<string>("common.entity.serviceevent.form.field.eventtype", "Event Type")}
                clearable
                withAsterisk
                required
                {...form.getInputProps("eventType")} />
            </Grid.Col>
            <Grid.Col span={12}>
              <TextInput
                size="xs"
                label={t("common.entity.serviceevent.form.field.name", "Name")}
                placeholder={t<string>("common.entity.serviceevent.form.field.name", "Name")}
                withAsterisk
                required
                {...form.getInputProps("name")} />
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider my="sm" variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={12}>
              <NumberInput
                size="xs"
                label={t("common.entity.serviceevent.form.field.severitylevel", "Severity Level")}
                placeholder={t<string>("common.entity.serviceevent.form.field.severitylevel", "Severity Level")}
                step={1}
                min={0}
                max={5}
                {...form.getInputProps("severityLevel")} />
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider my="sm" variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={6}>
              <DateInput
                size="xs"
                label={t("common.entity.serviceevent.form.field.start", "Start")}
                placeholder={t<string>("common.entity.serviceevent.form.field.type.start", "Start")}
                valueFormat="MM/DD/YYYY HH:mm"
                withAsterisk
                required
                clearable
                styles={{wrapper: {width: "100%"}}}
                {...form.getInputProps("startDate")} />
            </Grid.Col>
            <Grid.Col span={6}>
              <DateInput
                size="xs"
                label={t("common.entity.serviceevent.form.field.end", "End")}
                placeholder={t<string>("common.entity.serviceevent.form.field.type.end", "End")}
                valueFormat="MM/DD/YYYY HH:mm"
                clearable
                styles={{wrapper: {width: "100%"}}}
                {...form.getInputProps("endDate")} />
            </Grid.Col>
            <Grid.Col span={12}>
              <ServiceEventCloseoutPolicySelect
                size="xs"
                label={t("common.entity.serviceevent.form.field.closeoutpolicy", "Closeout Policy")}
                placeholder={t<string>("common.entity.serviceevent.form.field.closeoutpolicy", "Closeout Policy")}
                clearable
                withAsterisk
                required
                {...form.getInputProps("closeoutPolicy")} />
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider my="sm" variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea
                size="xs"
                label={t("common.entity.serviceevent.form.field.note", "Note")}
                placeholder={t<string>("common.entity.serviceevent.form.field.note.placeholder", "Note")}
                {...form.getInputProps("note")} />
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider my="sm" variant="dotted"/>
            </Grid.Col>
            <Grid.Col span={12}>
              <Group spacing="xs">
                <ActionIcon color="green" onClick={() => targetSelectHandler.open()}>
                  <IconPlus size={16}/>
                </ActionIcon>
                <Text weight={500} size="sm" pr={90}>
                  Targets
                </Text>
              </Group>
            </Grid.Col>
            <Grid.Col span={12}>
              {targetFields}
            </Grid.Col>
          </Grid>
          <Divider my="sm" variant="dotted"/>
          <Button type="submit" loading={submitting} fullWidth mt="xl">
            {t("common.action.submit", {ns: "common"})}
          </Button>
        </form>
      </Grid.Col>
      <Grid.Col span={6}>
        <Grid grow gutter="xs">
          {isMapLoaded &&
            <Grid.Col span={12}>
              <Text weight={500} size="sm" pr={90}>
                Target Boundaries
              </Text>
              {isLoading &&
                <Group spacing="xs">
                  <Loader size="xs" color="gray"/>
                  <Text size="sm" c="dimmed">
                    {t("common.state.refreshing", "Refreshing")}
                  </Text>
                </Group>
              }
              <Box
                style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}
              >
                <Map
                  onLoad={onMapLoad}
                  onUnmount={onMapUnmount}
                  zoom={15}
                  center={center}
                  mapContainerStyle={{width: "100%", height: '600px'}}
                  options={{
                    mapTypeId: "satellite",
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                >
                  <PolygonEditor
                    polygonOptions={{
                      fillColor: '#00FF00', // Green for user-drawn
                    }}
                    onPolygonComplete={onPolygonComplete}
                    onPolygonDelete={onPolygonDelete}
                    drawingMode={google.maps.drawing.OverlayType.POLYGON}/>

                  <SiteMarkerClustererMemo
                    entities={sites} />

                </Map>
              </Box>
            </Grid.Col>
          }
        </Grid>
      </Grid.Col>

      <TargetSelectModal
        opened={targetSelectOpened}
        onClose={targetSelectHandler.close}
        title={t("common.entity.serviceevent.form.action.addtarget", "Add Target")}
        onSubmit={onTargetAdd}
        select={{
          siteSelectorProps: {
            regionId: options?.regionId,
            company: form.values.company,
          },
          routeSelectorProps: {
            regionId: options?.regionId,
            companyId: form.values.company,
          },
          operatingAreaSelectorProps: {
            regionId: options?.regionId,
            companyId: form.values.company,
          },
          regionId: options?.regionId,
        }}
      />

    </Grid>
  );
};

export interface TargetSelectModalProps extends Omit<ModalProps, "onSubmit"> {
  select?: Omit<EntityMultiSelectProps, 'label' | 'placeholder' | 'withinPortal' | 'clearable' | 'onEntityChange'>,
  onSubmit?: (target: { entities: Entity[], mode: ServiceEventTargetMode } | undefined) => void
}

export const TargetSelectModal = (props: TargetSelectModalProps) => {
  const {
    select,
    onSubmit,
    ...rest
  } = props;

  const [selectedEntities, setSelectedEntities] = useState<Entity[]>([])

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: {
      entityType: undefined,
      entityIds: undefined,
      mode: serviceEventTargetMode.include.code,
    },
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        entities: selectedEntities,
        mode: toServiceEventTargetMode(values.mode)
      })
      form.reset()
    }
  }, [form, onSubmit, selectedEntities]);

  return (
    <Modal {...rest}>
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <Stack spacing="md">
          <EntityTypeSelect
            options={['SITE', 'ROUTE', 'OPERATINGAREA', 'REGION']}
            label={t("common.entity.siteassignment.form.field.object", "Object")}
            placeholder={t<string>("common.entity.siteassignment.form.field.object.placeholder", "Object")}
            withinPortal={true}
            clearable
            withAsterisk
            required
            {...form.getInputProps("entityType")} />
          <EntityMultiSelect
            {...select}
            onEntityChange={setSelectedEntities}
            pt="md"
            entityType={form.getTransformedValues().entityType}
            withinPortal={true}
            clearable
            withAsterisk
            required
            {...form.getInputProps("entityIds")} />
          <ServiceEventTargetModeSelect
            label={t("common.entity.serviceevent.form.field.target.mode", "Mode")}
            placeholder={t<string>("common.entity.serviceevent.form.field.target.mode", "Mode")}
            pt="md"
            withinPortal={true}
            clearable
            withAsterisk
            required
            {...form.getInputProps("mode")} />
          <Button type="submit" fullWidth mt="xl">
            {t("common.action.submit", {ns: "common"})}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};

function polygonPathsToGeoJSON(
  paths: google.maps.LatLngLiteral[][],
  properties?: Record<string, any>[] | Record<string, any>
): GeoJSON.FeatureCollection<GeoJSON.Polygon> {

  const features: GeoJSON.Feature<GeoJSON.Polygon>[] = paths.map((path, index) => {
    if (path.length < 3) {
      console.warn(`Path ${index} has fewer than 3 points, skipping.`);
      return undefined;
    }

    // Ensure the ring is closed (GeoJSON requires first = last point)
    const first = path[0];
    const last = path[path.length - 1];
    const coordinates: GeoJSON.Position[] = path.map(p => [p.lng, p.lat]);

    if (first.lat !== last.lat || first.lng !== last.lng) {
      coordinates.push([first.lng, first.lat]); // close the ring
    }

    // Get properties for this polygon
    let props: Record<string, any> = {};
    if (properties) {
      if (Array.isArray(properties)) {
        props = properties[index] || {};
      } else {
        props = properties;
      }
    }

    return {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: [coordinates]  // Single ring → [[outer]]
      },
      properties: props
    };
  }).filter(Boolean) as GeoJSON.Feature<GeoJSON.Polygon>[];

  return {
    type: "FeatureCollection",
    features
  };
}
